import { Box3, Group, Vector3 } from 'three';
import { cloneGltf, getPropAsset } from '../engine/loaders.js';

const box = new Box3();
const size = new Vector3();
const center = new Vector3();

const archetypes = [
  { id: 'barrier', asset: 'construction_barrier', height: [0.75, 1.05], tags: ['open', 'lanes', 'choke', 'gauntlet'], collidable: true },
  { id: 'cone', asset: 'construction_cone', height: [0.65, 0.95], tags: ['open', 'lanes'], collidable: true },
  { id: 'worklight', asset: 'construction_light', height: [1.05, 1.4], tags: ['open', 'lanes', 'choke'], collidable: true },
  { id: 'road-barrier', asset: 'road_barrier_half', height: [0.65, 0.95], tags: ['lanes', 'choke', 'gauntlet'], collidable: true },
  { id: 'light-curved', asset: 'street_light_curved', height: [3.2, 4.2], tags: ['open', 'rings'], collidable: true },
  { id: 'light-square', asset: 'street_light_square', height: [3.0, 3.9], tags: ['open', 'rings', 'gauntlet'], collidable: true },
  { id: 'highway-sign', asset: 'highway_sign', height: [2.2, 3.0], tags: ['rings', 'gauntlet'], collidable: true },
  { id: 'tank', asset: 'industrial_tank', height: [1.6, 2.25], tags: ['clusters', 'choke', 'gauntlet'], collidable: true },
  { id: 'chimney', asset: 'industrial_chimney', height: [2.0, 3.2], tags: ['clusters'], collidable: true },
  { id: 'parasol', asset: 'market_parasol', height: [1.2, 1.8], tags: ['lanes', 'clusters'], collidable: true },
  { id: 'awning', asset: 'market_awning', height: [1.0, 1.55], tags: ['lanes', 'clusters'], collidable: true },
  { id: 'building', asset: 'low_building', height: [2.8, 4.1], tags: ['clusters', 'choke'], collidable: true },
  { id: 'building-wide', asset: 'low_building_wide', height: [2.6, 3.9], tags: ['clusters', 'gauntlet'], collidable: true },
  { id: 'sedan', asset: 'car_sedan', height: [1.2, 1.65], tags: ['open', 'lanes', 'choke'], collidable: true },
  { id: 'taxi', asset: 'car_taxi', height: [1.2, 1.65], tags: ['open', 'rings', 'lanes'], collidable: true },
  { id: 'truck', asset: 'car_truck_flat', height: [1.45, 2.05], tags: ['choke', 'gauntlet'], collidable: true },
  { id: 'tire', asset: 'debris_tire', height: [0.5, 0.9], tags: ['open', 'clusters'], collidable: true },
  { id: 'door-debris', asset: 'debris_door', height: [0.35, 0.65], tags: ['open', 'clusters'], collidable: true },
  { id: 'road-box', asset: 'road_box', height: [0.55, 0.9], tags: ['open', 'lanes'], collidable: true },
];

const patternTags = {
  open: ['open', 'lanes', 'rings'],
  rings: ['rings', 'open'],
  lanes: ['lanes', 'open'],
  choke: ['choke', 'gauntlet', 'lanes'],
  clusters: ['clusters', 'open'],
  gauntlet: ['gauntlet', 'choke', 'lanes'],
};

export function pickModelPropArchetype(rng, pattern = 'open', index = 0) {
  const tags = patternTags[pattern] || patternTags.open;
  const candidates = archetypes.filter((item) => item.tags.some((tag) => tags.includes(tag)));
  if (!candidates.length) return archetypes[index % archetypes.length];
  return candidates[Math.floor(rng() * candidates.length)];
}

export function createModelProp(archetype, placement) {
  const resolved = getPropAsset(archetype.asset);
  const source = resolved?.asset?.scene || resolved?.asset;
  if (!source) return null;

  const model = cloneGltf(source);
  const group = new Group();
  group.name = `prop-${archetype.id}`;
  group.add(model);

  model.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
  });

  box.setFromObject(model);
  box.getSize(size);
  if (size.y <= 0.0001) return null;

  const targetHeight = placement.height || resolved.entry?.height || 1;
  const scale = targetHeight / size.y;
  model.scale.multiplyScalar(scale);

  box.setFromObject(model);
  box.getCenter(center);
  model.position.x -= center.x;
  model.position.z -= center.z;
  model.position.y -= box.min.y;

  group.position.set(placement.x, 0, placement.z);
  group.rotation.y = placement.rotation + (resolved.entry?.rotateY || archetype.rotateY || 0);
  group.userData.collidable = archetype.collidable !== false;
  group.userData.surface = resolved.entry?.surface || archetype.surface || 'metal';
  group.userData.propAsset = archetype.asset;
  group.userData.modelProp = true;

  finalizeBounds(group);
  return group;
}

export function finalizeBounds(object) {
  object.updateMatrixWorld(true);
  box.setFromObject(object);
  box.getSize(size);
  object.userData.radius = Math.max(size.x, size.z) * 0.5;
  object.userData.aabb = {
    minX: box.min.x,
    maxX: box.max.x,
    minZ: box.min.z,
    maxZ: box.max.z,
  };
}
